// Copyright 2026 Holmes Computer Consultants. All rights reserved.
// Fixed original print assets only. No player-authored URL or upload enters this adapter.

export interface PrintPixels { readonly width: number; readonly height: number; readonly pixels: Uint8ClampedArray }
type PrintSurface = 'backglass' | 'playfield' | 'cabinet' | 'cabinetRight' | 'front' | 'hull'
interface PrintPair { readonly backglass: PrintPixels; readonly playfield: PrintPixels; readonly cabinet: PrintPixels; readonly cabinetRight?: PrintPixels; readonly front?: PrintPixels; readonly hull?: PrintPixels }
const sources = [
  [new URL('../../SourceArt/CoastalPrints/dune-backglass.jpg', import.meta.url).href, new URL('../../SourceArt/CoastalPrints/dune-playfield.jpg', import.meta.url).href, new URL('../../SourceArt/CoastalPrints/dune-cabinet.jpg', import.meta.url).href],
  [new URL('../../SourceArt/CoastalPrints/surf-backglass.jpg', import.meta.url).href, new URL('../../SourceArt/CoastalPrints/surf-playfield.jpg', import.meta.url).href, new URL('../../SourceArt/CoastalPrints/surf-cabinet.jpg', import.meta.url).href],
  [new URL('../../SourceArt/CoastalPrints/pier-backglass.jpg', import.meta.url).href, new URL('../../SourceArt/CoastalPrints/pier-playfield.jpg', import.meta.url).href, new URL('../../SourceArt/CoastalPrints/pier-cabinet.jpg', import.meta.url).href],
  [new URL('../../SourceArt/StarfallSalvage/starfall-backglass.jpg', import.meta.url).href, new URL('../../SourceArt/StarfallSalvage/starfall-playfield.jpg', import.meta.url).href, new URL('../../SourceArt/StarfallSalvage/starfall-cabinet.jpg', import.meta.url).href, new URL('../../SourceArt/StarfallSalvage/starfall-cabinetRight.jpg', import.meta.url).href, new URL('../../SourceArt/StarfallSalvage/starfall-front.jpg', import.meta.url).href, new URL('../../SourceArt/StarfallSalvage/starfall-hull.jpg', import.meta.url).href],
] as const
const keys = ['dune', 'surf', 'pier', 'starfall'] as const
const pairs = new Map<number, PrintPair>()
const pending = new Map<number, Promise<void>>()

async function decode(tile: number, surface: PrintSurface): Promise<PrintPixels> {
  const metadata = (tile === 3 ? (await import('../../SourceArt/StarfallSalvage/manifest.json')).default : (await import('../../SourceArt/CoastalPrints/manifest.json')).default).files.find(file => file.name === keys[tile] + '-' + surface)
  const url = sources[tile]?.[surface === 'backglass' ? 0 : surface === 'playfield' ? 1 : surface === 'cabinet' ? 2 : surface === 'cabinetRight' ? 3 : surface === 'front' ? 4 : 5]
  if (metadata === undefined || url === undefined) throw new Error('Unknown coastal print')
  if (new URL(url).origin !== location.origin) throw new Error('Coastal print must be same-origin')
  // An exact content identity prevents an older same-path development print being reused.
  const requestUrl = new URL(url); requestUrl.searchParams.set('sha256', metadata.packagedSha256)
  const response = await fetch(requestUrl.href, { credentials: 'omit', mode: 'same-origin', redirect: 'error', referrerPolicy: 'no-referrer', cache: 'force-cache' })
  if (!response.ok) throw new Error('Coastal print unavailable')
  const bytes = await readCoastalPrintBytes(response, metadata.packagedBytes)
  if (bytes.byteLength !== metadata.packagedBytes || bytes.byteLength > 1048576) throw new Error('Coastal print byte bounds')
  const sha = await crypto.subtle.digest('SHA-256', bytes)
  const hash = [...new Uint8Array(sha)].map(b => b.toString(16).padStart(2, '0')).join('')
  if (hash !== metadata.packagedSha256) throw new Error('Coastal print hash mismatch')
  const bitmap = await createImageBitmap(new Blob([bytes], { type: 'image/jpeg' }))
  try {
    if (bitmap.width !== metadata.width || bitmap.height !== metadata.height) throw new Error('Coastal print dimension mismatch')
    const canvas = document.createElement('canvas')
    canvas.width = bitmap.width; canvas.height = bitmap.height
    const context = canvas.getContext('2d', { willReadFrequently: true })
    if (context === null) throw new Error('Coastal print canvas unavailable')
    context.drawImage(bitmap, 0, 0)
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data
    return { width: canvas.width, height: canvas.height, pixels }
  } finally { bitmap.close() }
}

/** Read a fixed packaged image with a strict allocation/stream cap before bitmap decoding. */
export async function readCoastalPrintBytes(response: Response, expectedBytes: number): Promise<ArrayBuffer> {
  if (!Number.isInteger(expectedBytes) || expectedBytes <= 0 || expectedBytes > 1048576)
    throw new Error('Coastal print byte bounds')
  const declared = response.headers.get('content-length')
  if (declared !== null && (!/^\d+$/.test(declared) || Number(declared) > 1048576))
    throw new Error('Coastal print byte bounds')
  if (response.body === null) throw new Error('Coastal print stream unavailable')
  const bytes = new Uint8Array(expectedBytes)
  const reader = response.body.getReader()
  let offset = 0
  try {
    while (true) {
      const next = await reader.read()
      if (next.done) break
      if (offset + next.value.byteLength > expectedBytes) throw new Error('Coastal print byte bounds')
      bytes.set(next.value, offset); offset += next.value.byteLength
    }
    if (offset !== expectedBytes) throw new Error('Coastal print byte bounds')
    return bytes.buffer
  } catch (error) {
    await reader.cancel().catch(() => undefined)
    throw error
  } finally { reader.releaseLock() }
}

/** Decode only the selected three-surface set; cache contains at most three catalogue entries. */
export async function prepareCoastalPrint(tile: number): Promise<void> {
  if (!Number.isInteger(tile) || tile < 0 || tile >= sources.length) throw new Error('Coastal print tile')
  let promise = pending.get(tile)
  if (promise === undefined) {
    promise = Promise.all([decode(tile, 'backglass'), decode(tile, 'playfield'), decode(tile, 'cabinet'), ...(tile === 3 ? [decode(tile, 'cabinetRight'), decode(tile, 'front'),decode(tile,'hull')] : [])]).then(([backglass, playfield, cabinet, cabinetRight, front, hull]) => {
      if (backglass === undefined || playfield === undefined || cabinet === undefined) throw new Error('Incomplete print set')
      pairs.set(tile, { backglass, playfield, cabinet, ...(cabinetRight === undefined ? {} : { cabinetRight }), ...(front === undefined ? {} : { front }), ...(hull === undefined ? {} : { hull }) })
    }).catch(error => { pending.delete(tile); throw error })
    pending.set(tile, promise)
  }
  await promise
}
export function coastalPrint(tile: number, surface: PrintSurface): PrintPixels | undefined { return pairs.get(tile)?.[surface] }

/** Bilinear sampling keeps the whole print; original-colour prints receive only scene lighting. */
export function paintPrintPixels(ctx: CanvasRenderingContext2D, width: number, height: number, source: PrintPixels, playfield: boolean, physicalAspect?: number, mirror = false, titleBand = !playfield, preserveColours = false): void {
  const image = ctx.createImageData(width, height)
  const sourceAspect = source.width / source.height
  const fit = printFit(sourceAspect, physicalAspect ?? (playfield ? width/height : sourceAspect))
  const cropX = fit.x, cropY = fit.y
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    const u = mirror ? 1-x/Math.max(1,width-1) : x/Math.max(1,width-1)
    const sx = (.5 + (u - .5) * cropX) * (source.width - 1)
    const sy = (.5 + (y / Math.max(1, height - 1) - .5) * cropY) * (source.height - 1)
    const x0 = Math.floor(sx), y0 = Math.floor(sy), x1 = Math.min(x0 + 1, source.width - 1), y1 = Math.min(y0 + 1, source.height - 1)
    const fx = sx - x0, fy = sy - y0, dst = (y * width + x) * 4
    const exposure = preserveColours ? 1 : playfield ? .58 - .20 * y / Math.max(1, height - 1) : titleBand && y < height * .22 ? .12 : .75
    for (let c = 0; c < 3; c++) {
      const top = source.pixels[(y0 * source.width + x0) * 4 + c]! * (1 - fx) + source.pixels[(y0 * source.width + x1) * 4 + c]! * fx
      const bottom = source.pixels[(y1 * source.width + x0) * 4 + c]! * (1 - fx) + source.pixels[(y1 * source.width + x1) * 4 + c]! * fx
      image.data[dst + c] = Math.floor((top * (1 - fy) + bottom * fy) * exposure)
    }
    image.data[dst + 3] = 255
  }
  ctx.putImageData(image, 0, 0)
}

/** Match ink proportions to metres on the panel, never to the GPU texture's pixel shape. */
export function printFit(sourceAspect: number, panelAspect: number): { readonly x: number; readonly y: number } {
  if (!Number.isFinite(sourceAspect) || sourceAspect<=0 || !Number.isFinite(panelAspect) || panelAspect<=0)
    throw new Error('positive finite print aspect required')
  return { x: Math.min(1,panelAspect/sourceAspect), y: Math.min(1,sourceAspect/panelAspect) }
}
